/**
 * FirmWideBillingSection — the Admin → Fees → "Billing details" firm-wide surface
 * (OPEN_ITEMS #8). One table of EVERY billable account across all agreements with its
 * billing identity, fee-schedule assignment, basis flags and resulting-fee preview —
 * the cross-agreement oversight view you cannot get one agreement at a time.
 *
 * v2 (2026-07-04, post-smoke): GROUPED BY AGREEMENT with expand/collapse (one + all;
 * default collapsed = the per-agreement overview), PER-COLUMN header filters under the
 * sortable labels (the shared ColumnFilters primitive, C-132), quick cross-cutting
 * chips (kept — Pedro found them useful), and pills rendered UNDER the field they refer
 * to (Pedro's smoke polish). Prospect agreements ARE listed (P1, 2026-10-09) so their
 * accounts can be given a fee schedule before promotion; they carry a "Prospect · not
 * billed yet" badge and are left out of the "unbilled" warning (they are never billed).
 *
 * READ-ONLY (Inc 1–3). Editing (per-row + apply-to-checked, assignment AND identity)
 * is Inc 4; the Agreement → Fees section becomes a read-only mirror in Inc 5.
 *
 * All drift / completeness / basis flags and the resulting fee are SERVER-DERIVED
 * (FirmWideBillingRow) — never recomputed here (C-214). Friendly labels throughout
 * (§3j): agreement code · mnemonic, bank · number, no UUIDs. Volume is modest
 * (<=250 rows, Pedro) so the whole list is fetched once and all work is in-browser.
 */
import { Fragment, useMemo, useState } from 'react';
import {
  ActionIcon, Alert, Badge, Button, Checkbox, Chip, Group, Loader, NumberInput, Paper,
  Select, Stack, Table, Text, TextInput, Title, Tooltip, UnstyledButton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAddressBook, IconAlertTriangle, IconCheck, IconChevronDown, IconChevronRight,
  IconChevronUp, IconChevronsDown, IconChevronsUp, IconCircleX, IconDownload,
  IconFilterOff, IconPencil, IconSearch, IconSelector,
} from '@tabler/icons-react';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import { accountBillingApi, type BasisPeer, type FirmWideBillingRow } from '../api/accountBilling';
import { bpsToPct, feeSchedulesApi, fmtEurFromCents, pctToBps } from '../api/feeSchedules';
import { BillingRowEditor } from './BillingRowEditor';
import {
  ColumnSelectFilter, ColumnTextFilter, FilteredColumnLabel, hasActiveFilters, textMatches,
} from './ColumnFilters';
import { StatusExplained } from './StatusExplained';

const FIRM_WIDE_KEY = ['account-billing', 'firm-wide'] as const;

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function fmtMonthYear(iso: string): string {
  const [y, m] = iso.split('-');
  return `${MONTH_ABBR[Number(m) - 1] ?? m}-${y}`;
}
const FREQ_LABEL: Record<string, string> = {
  monthly: 'Monthly', quarterly: 'Quarterly', semiannual: 'Semiannual',
};

const SENTINEL_UNASSIGNED = '__unassigned__';
const SENTINEL_UNSET = '__unset__';
const SENTINEL_NO_VAT = '__novat__';
// Quick cross-cutting predicate chips — each narrows to one oversight concern.
const FLAG_CHIPS: { value: string; label: string }[] = [
  { value: 'unbilled', label: 'Unbilled (Active, no schedule)' },
  { value: 'incomplete', label: 'Incomplete identity' },
  { value: 'missing_aum', label: 'Missing 20th AuM' },
  { value: 'drift', label: 'Drift' },
];

// "Unbilled" = an Active account with no schedule, so it would silently not bill. A
// Prospect's accounts are never billed, so they are not counted here (P1).
function isUnbilled(r: FirmWideBillingRow): boolean {
  return r.agreement_status !== 'Prospect' && r.status === 'Active' && r.fee_schedule_id == null;
}

// What each billing issue means for the invoice — shared by the badges and the summary box.
const ISSUE_TIP = {
  unbilled: 'Active account with no fee schedule — it will NOT be billed. Assign a schedule.',
  missing_aum: 'Has a fee schedule but no 20th-of-month AuM for the latest month — no fee will be computed until the AuM is uploaded.',
  incomplete: 'Billing name, TIN or address is missing — the fee computes, but the invoice cannot be issued correctly. Complete the billing identity.',
} as const;

function agreementLabel(r: FirmWideBillingRow): string {
  return [r.agreement_code, r.agreement_mnemonic].filter(Boolean).join(' · ') || '—';
}
function accountLabel(r: FirmWideBillingRow): string {
  const bank = r.bank_name || 'Account';
  return r.account_number ? `${bank} — ${r.account_number}` : bank;
}

// ── per-column filter state ─────────────────────────────────────────────────
interface ColFilters {
  account: string; type: string | null; status: string | null;
  billingName: string; tin: string; address: string;
  completeness: string | null; schedule: string | null; frequency: string | null; vat: string | null;
}
const EMPTY_FILTERS: ColFilters = {
  account: '', type: null, status: null, billingName: '', tin: '', address: '',
  completeness: null, schedule: null, frequency: null, vat: null,
};
// B2 polish: the page's resting state shows Active accounts only (Prospects are
// already excluded server-side). Status is a top-level *lens* — it does NOT count
// as a narrowing filter (so it never force-expands the groups), and "Clear filters"
// returns here, not to EMPTY_FILTERS.
const DEFAULT_FILTERS: ColFilters = { ...EMPTY_FILTERS, status: 'Active' };

// ── sorting (applies to account rows WITHIN each agreement group) ────────────
type SortKey = 'account' | 'billing_name' | 'completeness' | 'schedule' | 'frequency' | 'vat' | 'fee';
function sortValue(r: FirmWideBillingRow, key: SortKey): string | number {
  switch (key) {
    case 'account': return `${r.bank_name ?? ''} ${r.account_number ?? ''}`.toLowerCase();
    case 'billing_name': return (r.billing_name ?? '').toLowerCase();
    case 'completeness': return r.is_complete ? 1 : 0;
    case 'schedule': return (r.fee_schedule_name ?? '').toLowerCase();
    case 'frequency': return r.fee_frequency ?? '';
    case 'vat': return r.vat_rate_bps ?? -1;
    case 'fee': return r.total_fee_cents ?? -1;
  }
}

interface AgreementGroup {
  agreementId: string;
  code: string | null;
  mnemonic: string;
  rows: FirmWideBillingRow[];
}

export function FirmWideBillingSection() {
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const q = useQuery({ queryKey: FIRM_WIDE_KEY, queryFn: () => accountBillingApi.listFirmWide() });
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'account', dir: 'asc' });
  const [filters, setFilters] = useState<ColFilters>(DEFAULT_FILTERS);
  // Pre-filter to one agreement when arriving via the Agreement -> Fees "Manage in
  // Admin -> Fees" deep-link (?agreement=<id>); otherwise show all (Inc 5).
  const [agreementFilter, setAgreementFilter] = useState<string | null>(() => searchParams.get('agreement'));
  const [flags, setFlags] = useState<string[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());  // per-agreement open
  const [expandedPeers, setExpandedPeers] = useState<Set<string>>(new Set());    // per-account basis open
  const [editingKey, setEditingKey] = useState<string | null>(null);            // per-row inline editor (one at a time)
  const [selected, setSelected] = useState<Set<string>>(new Set());             // apply-to-checked selection
  const [bulkFreq, setBulkFreq] = useState<string | null>(null);
  const [bulkVat, setBulkVat] = useState<number | ''>('');
  const [bulkScheduleId, setBulkScheduleId] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const setF = <K extends keyof ColFilters>(k: K, v: ColFilters[K]) =>
    setFilters((f) => ({ ...f, [k]: v }));
  const toggleSort = (key: SortKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  const rowKey = (r: FirmWideBillingRow) => `${r.agreement_id}:${r.account_id}`;
  const toggleGroup = (aid: string) =>
    setExpandedGroups((prev) => { const n = new Set(prev); n.has(aid) ? n.delete(aid) : n.add(aid); return n; });
  const togglePeers = (key: string) =>
    setExpandedPeers((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });

  // Option lists for the Selects (from the data present).
  const agreementOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of q.data ?? []) if (!seen.has(r.agreement_id)) seen.set(r.agreement_id, agreementLabel(r));
    return [...seen.entries()].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [q.data]);
  const scheduleOptions = useMemo(() => {
    const names = new Set<string>();
    for (const r of q.data ?? []) if (r.fee_schedule_name) names.add(r.fee_schedule_name);
    return [{ value: SENTINEL_UNASSIGNED, label: 'Unassigned' }, ...[...names].sort().map((n) => ({ value: n, label: n }))];
  }, [q.data]);
  const vatOptions = useMemo(() => {
    const bps = new Set<number>();
    let anyNull = false;
    for (const r of q.data ?? []) { if (r.vat_rate_bps == null) anyNull = true; else bps.add(r.vat_rate_bps); }
    const opts = [...bps].sort((a, b) => a - b).map((b) => ({ value: String(b), label: `${bpsToPct(b).toFixed(2)}%` }));
    return anyNull ? [{ value: SENTINEL_NO_VAT, label: 'None' }, ...opts] : opts;
  }, [q.data]);

  // autoExpand = a *narrowing* filter is applied (search / flag / deep-link / any
  // column filter EXCEPT status) → force groups open so matches are visible.
  // The status lens (Active/Inactive/Any) is excluded: switching it re-scopes the
  // list but keeps the collapsed overview and the Expand/Collapse-all controls live.
  const autoExpand =
    search.trim() !== '' || flags.length > 0 || agreementFilter != null
    || hasActiveFilters({ ...filters, status: null } as unknown as Record<string, string | string[] | null>);
  // filtersActive = state differs from the resting default (status=Active, nothing
  // else) → show "Clear filters". A non-default status lens counts here even though
  // it doesn't auto-expand.
  const filtersActive = autoExpand || filters.status !== 'Active';

  const rowMatches = (r: FirmWideBillingRow): boolean => {
    if (agreementFilter && r.agreement_id !== agreementFilter) return false;
    // P41: textMatches — "-" finds blanks, otherwise an accent-insensitive substring.
    if (!textMatches(accountLabel(r), filters.account)) return false;
    if (filters.type && (r.is_direct ? 'Direct' : 'Indirect') !== filters.type) return false;
    if (filters.status && r.status !== filters.status) return false;
    if (!textMatches(r.billing_name, filters.billingName)) return false;
    if (!textMatches(r.billing_tin, filters.tin)) return false;
    if (!textMatches(r.billing_address, filters.address)) return false;
    if (filters.completeness && (filters.completeness === 'complete') !== r.is_complete) return false;
    if (filters.schedule) {
      if (filters.schedule === SENTINEL_UNASSIGNED) { if (r.fee_schedule_id != null) return false; }
      else if (r.fee_schedule_name !== filters.schedule) return false;
    }
    if (filters.frequency) {
      if (filters.frequency === SENTINEL_UNSET) { if (r.fee_frequency != null) return false; }
      else if (r.fee_frequency !== filters.frequency) return false;
    }
    if (filters.vat) {
      if (filters.vat === SENTINEL_NO_VAT) { if (r.vat_rate_bps != null) return false; }
      else if (String(r.vat_rate_bps) !== filters.vat) return false;
    }
    if (flags.includes('unbilled') && !isUnbilled(r)) return false;
    if (flags.includes('incomplete') && r.is_complete) return false;
    if (flags.includes('missing_aum') && !r.missing_latest_aum) return false;
    if (flags.includes('drift') && !(r.name_drift || r.tin_drift || r.address_drift)) return false;
    const ql = search.trim().toLowerCase();
    if (ql
      && !agreementLabel(r).toLowerCase().includes(ql)
      && !accountLabel(r).toLowerCase().includes(ql)
      && !(r.billing_name ?? '').toLowerCase().includes(ql)
      && !(r.fee_schedule_name ?? '').toLowerCase().includes(ql)) return false;
    return true;
  };

  // Group the matching rows by agreement; sort rows within each group; order groups by code.
  const groups = useMemo<AgreementGroup[]>(() => {
    const byAgr = new Map<string, AgreementGroup>();
    for (const r of q.data ?? []) {
      if (!rowMatches(r)) continue;
      let g = byAgr.get(r.agreement_id);
      if (!g) { g = { agreementId: r.agreement_id, code: r.agreement_code, mnemonic: r.agreement_mnemonic, rows: [] }; byAgr.set(r.agreement_id, g); }
      g.rows.push(r);
    }
    const dir = sort.dir === 'asc' ? 1 : -1;
    for (const g of byAgr.values()) {
      g.rows.sort((a, b) => {
        const va = sortValue(a, sort.key); const vb = sortValue(b, sort.key);
        if (va < vb) return -1 * dir;
        if (va > vb) return 1 * dir;
        return accountLabel(a).localeCompare(accountLabel(b));
      });
    }
    return [...byAgr.values()].sort((a, b) => (a.code ?? '').localeCompare(b.code ?? '') || a.mnemonic.localeCompare(b.mnemonic));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data, search, sort, filters, agreementFilter, flags]);

  const total = q.data?.length ?? 0;
  // Firm-wide billing-at-risk tally for the summary box (unfiltered; Active, non-Prospect only).
  const issues = useMemo(() => {
    const live = (q.data ?? []).filter((r) => r.agreement_status !== 'Prospect' && r.status === 'Active');
    const unbilled = live.filter(isUnbilled);
    const missing = live.filter((r) => r.missing_latest_aum);
    const incomplete = live.filter((r) => !r.is_complete);
    const agrs = new Set([...unbilled, ...missing, ...incomplete].map((r) => r.agreement_id));
    return { unbilled: unbilled.length, missingAum: missing.length, incomplete: incomplete.length, agreements: agrs.size };
  }, [q.data]);
  const shownAccounts = groups.reduce((n, g) => n + g.rows.length, 0);
  // When a narrowing filter/search is applied, force groups open so matches are
  // visible; otherwise respect the user's per-group expand state (default
  // collapsed = overview). The status lens alone does NOT force-expand, so
  // Collapse all works in the default Active view.
  const isGroupOpen = (aid: string) => autoExpand || expandedGroups.has(aid);
  const expandAll = () => setExpandedGroups(new Set(groups.map((g) => g.agreementId)));
  const collapseAll = () => setExpandedGroups(new Set());
  const clearAll = () => { setFilters(DEFAULT_FILTERS); setAgreementFilter(null); setFlags([]); setSearch(''); };

  // ── apply-to-checked bulk-assign (Inc 4b/4c) ───────────────────────────────
  const shownRows = useMemo(() => groups.flatMap((g) => g.rows), [groups]);
  const shownKeys = useMemo(() => shownRows.map(rowKey), [shownRows]);
  const selectedRows = useMemo(() => shownRows.filter((r) => selected.has(rowKey(r))), [shownRows, selected]);
  const selectedAgrIds = useMemo(() => [...new Set(selectedRows.map((r) => r.agreement_id))], [selectedRows]);
  const agrLabelById = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of shownRows) if (!m.has(r.agreement_id)) m.set(r.agreement_id, agreementLabel(r));
    return m;
  }, [shownRows]);
  const allSelected = shownKeys.length > 0 && shownKeys.every((k) => selected.has(k));
  const someSelected = shownKeys.some((k) => selected.has(k));
  const toggleSelect = (key: string) =>
    setSelected((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const toggleSelectAll = () => setSelected(allSelected ? new Set() : new Set(shownKeys));
  const clearSelection = () => { setSelected(new Set()); setBulkScheduleId(null); };

  // For bulk SCHEDULE assign: which schedules are available to EACH selected agreement
  // (one query per distinct selected agreement). The dropdown offers the UNION; on Apply
  // we require the chosen schedule to be available to EVERY selected agreement (Pedro
  // 2026-07-04, all-or-nothing with a naming warning). Frequency/VAT are always applicable.
  const availQueries = useQueries({
    queries: selectedAgrIds.map((a) => ({
      queryKey: ['fee-schedules', 'available', a],
      queryFn: () => feeSchedulesApi.availableForAgreement(a),
    })),
  });
  const availLoading = availQueries.some((qq) => qq.isLoading);
  const availByAgr = new Map<string, Set<string>>();
  selectedAgrIds.forEach((a, i) => availByAgr.set(a, new Set((availQueries[i]?.data ?? []).map((s) => s.id))));
  const bulkScheduleOptions = (() => {
    const names = new Map<string, string>();
    availQueries.forEach((qq) => (qq.data ?? []).forEach((s) => { if (!names.has(s.id)) names.set(s.id, s.name ?? '(unnamed)'); }));
    return [...names.entries()].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  })();

  async function applyBulk() {
    const rows = selectedRows;
    const settingSchedule = bulkScheduleId != null;
    if (rows.length === 0) return;
    if (!settingSchedule && bulkFreq == null && bulkVat === '') return;

    // All-or-nothing schedule guard: apply a schedule only if available to EVERY selected
    // agreement; otherwise apply nothing and name the agreements it can't apply to.
    if (settingSchedule) {
      const offending = selectedAgrIds.filter((a) => !availByAgr.get(a)?.has(bulkScheduleId!));
      if (offending.length > 0) {
        const names = offending.map((a) => agrLabelById.get(a) ?? a);
        notifications.show({
          color: 'red', title: 'Schedule not available to all selected', autoClose: false,
          message: `That fee schedule is not available to ${offending.length} of the selected agreement(s) - nothing was applied. Grant its availability there first (Admin -> Fees -> Fee Schedules), or narrow the selection. Not available to: ${names.slice(0, 6).join(', ')}${names.length > 6 ? '…' : ''}.`,
        });
        return;
      }
    }

    setBulkBusy(true);
    const failed: string[] = [];
    const agrIds = new Set<string>();
    for (const r of rows) {
      agrIds.add(r.agreement_id);
      try {
        await accountBillingApi.setAssignment(r.agreement_id, r.account_id, {
          fee_schedule_id: settingSchedule ? bulkScheduleId : r.fee_schedule_id,
          vat_rate_bps: bulkVat === '' ? r.vat_rate_bps : pctToBps(bulkVat),
          fee_frequency: bulkFreq ?? r.fee_frequency,
        });
      } catch {
        failed.push(`${r.bank_name ?? 'account'} ${r.account_number ?? ''}`.trim());
      }
    }
    await qc.invalidateQueries({ queryKey: FIRM_WIDE_KEY });
    await Promise.all([...agrIds].flatMap((a) => [
      qc.invalidateQueries({ queryKey: ['account-billing', a] }),
      qc.invalidateQueries({ queryKey: ['account-fee-preview', a] }),
    ]));
    setBulkBusy(false);
    const ok = rows.length - failed.length;
    if (failed.length === 0) {
      notifications.show({ color: 'green', message: `Applied to ${ok} account${ok === 1 ? '' : 's'}.` });
      clearSelection(); setBulkFreq(null); setBulkVat(''); setBulkScheduleId(null);
    } else {
      notifications.show({
        color: 'orange', title: 'Applied with errors', autoClose: false,
        message: `${ok} updated, ${failed.length} failed: ${failed.slice(0, 5).join(', ')}${failed.length > 5 ? '…' : ''}. Selection kept.`,
      });
    }
  }

  // Oversight snapshot -> styled server-side .xlsx (matches the app's other exports).
  // Send the on-screen rows in display order so the sheet mirrors the CURRENT
  // filtered/sorted view (the value Pedro liked from the CSV version).
  const [exporting, setExporting] = useState(false);
  const exportData = async () => {
    const keys = groups.flatMap((g) => g.rows).map((r) => ({ agreement_id: r.agreement_id, account_id: r.account_id }));
    setExporting(true);
    try {
      await accountBillingApi.exportFirmWide(keys);
    } catch {
      notifications.show({ color: 'red', message: 'The billing export could not be generated.' });
    } finally {
      setExporting(false);
    }
  };

  const COLSPAN = 13;  // expander + 11 data columns + trailing edit-action column

  return (
    <Stack mt="md">
      <Group justify="space-between" align="flex-end">
        <Group gap="xs">
          <IconAddressBook size={20} />
          <Title order={4}>Billing details — firm-wide</Title>
          <Text size="sm" c="dimmed">every billable account, grouped by agreement</Text>
        </Group>
        <Group gap="sm" align="flex-end">
          <TextInput
            w={260} leftSection={<IconSearch size={14} />}
            placeholder="Search agreement, account, payee, schedule"
            value={search} onChange={(e) => setSearch(e.currentTarget.value)}
          />
          <Button variant="default" leftSection={<IconDownload size={16} />} onClick={exportData} loading={exporting} disabled={shownAccounts === 0}>
            Export data
          </Button>
        </Group>
      </Group>

      {q.isLoading && <Text c="dimmed">Loading…</Text>}
      {q.isError && <Alert color="red" variant="light">Could not load the firm-wide billing table.</Alert>}
      {!q.isLoading && !q.isError && total === 0 && (
        <Alert color="gray" variant="light">
          No accounts yet. A row appears once an account is linked to an agreement — directly,
          or indirectly through an insurance policy. Prospects are listed so they can be given
          a fee schedule before promotion; they are not billed until promoted.
        </Alert>
      )}

      {total > 0 && issues.agreements > 0 && (
        <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />}
          title={`Billing at risk — ${issues.agreements} agreement${issues.agreements === 1 ? '' : 's'} need${issues.agreements === 1 ? 's' : ''} attention`}>
          <Stack gap={4}>
            {([
              ['unbilled', issues.unbilled, 'will not be billed (no fee schedule)'],
              ['missing_aum', issues.missingAum, 'will not compute a fee (no 20th AuM for the latest month)'],
              ['incomplete', issues.incomplete, 'have incomplete billing identity (invoice cannot be issued correctly)'],
            ] as const).filter(([, n]) => n > 0).map(([flag, n, text]) => (
              <Group key={flag} gap="xs">
                <Text size="sm"><b>{n} account{n === 1 ? '' : 's'}</b> {text}</Text>
                <Button size="compact-xs" variant={flags.includes(flag) ? 'filled' : 'light'} color="red"
                  onClick={() => setFlags((f) => (f.includes(flag) ? f.filter((x) => x !== flag) : [...f, flag]))}>
                  {flags.includes(flag) ? 'Showing' : 'Show'}
                </Button>
              </Group>
            ))}
            <Text size="xs" c="dimmed">Active agreements only — Prospects are not billed until promoted.</Text>
          </Stack>
        </Alert>
      )}

      {total > 0 && (
        <>
          <Group gap="sm" align="flex-end" wrap="wrap">
            <ColumnSelectFilter
              value={agreementFilter} onChange={setAgreementFilter}
              options={agreementOptions} allLabel="Any agreement" ariaLabel="Filter by agreement"
            />
            <Chip.Group multiple value={flags} onChange={setFlags}>
              <Group gap="xs">
                {FLAG_CHIPS.map((c) => <Chip key={c.value} value={c.value} size="xs" variant="outline">{c.label}</Chip>)}
              </Group>
            </Chip.Group>
            <Button variant="subtle" color="gray" size="compact-sm" leftSection={<IconChevronsDown size={14} />} onClick={expandAll}>
              Expand all
            </Button>
            <Button variant="subtle" color="gray" size="compact-sm" leftSection={<IconChevronsUp size={14} />} onClick={collapseAll}>
              Collapse all
            </Button>
            {(filtersActive) && (
              <Button variant="subtle" color="gray" size="compact-sm" leftSection={<IconFilterOff size={14} />} onClick={clearAll}>
                Clear filters
              </Button>
            )}
          </Group>

          <Text size="xs" c="dimmed">
            {shownAccounts === total ? `${total} accounts in ${groups.length} agreements`
              : `${shownAccounts} of ${total} accounts · ${groups.length} agreements shown`}
            {autoExpand ? ' · filtered (groups expanded)' : ''}
          </Text>

          {selected.size > 0 && (
            <Paper withBorder p="sm" radius="md" bg="var(--mantine-color-blue-0)">
              <Group gap="sm" align="flex-end" wrap="wrap">
                <Text size="sm" fw={600}>{selected.size} selected</Text>
                <Select
                  label="Set fee schedule" w={220}
                  placeholder={availLoading ? 'Checking availability…' : '— (leave unchanged)'}
                  data={bulkScheduleOptions} value={bulkScheduleId} onChange={setBulkScheduleId}
                  clearable disabled={availLoading}
                />
                <Select
                  label="Set frequency" w={150} placeholder="—"
                  data={[{ value: 'monthly', label: 'Monthly' }, { value: 'quarterly', label: 'Quarterly' }, { value: 'semiannual', label: 'Semiannual' }]}
                  value={bulkFreq} onChange={setBulkFreq} clearable
                />
                <NumberInput
                  label="Set VAT" w={110} min={0} max={100} step={0.01} decimalScale={2} suffix=" %"
                  value={bulkVat} onChange={(v) => setBulkVat(typeof v === 'number' ? v : '')}
                />
                <Button color="azul" loading={bulkBusy}
                  disabled={availLoading || (bulkScheduleId == null && bulkFreq == null && bulkVat === '')}
                  onClick={applyBulk}>
                  Apply to selected
                </Button>
                <Button variant="subtle" color="gray" onClick={clearSelection}>Clear selection</Button>
              </Group>
              <Text size="xs" c="dimmed" mt={4}>
                Sets the chosen fee schedule / frequency / VAT on the selected accounts (each
                committed directly). A fee schedule applies only if it is available to <b>every</b>{' '}
                selected agreement — otherwise nothing is applied and the agreements it can't apply
                to are named. Leave a control blank to keep each account's current value.
              </Text>
            </Paper>
          )}

          <Table.ScrollContainer minWidth={1250}>
            <Table withTableBorder highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={36} />
                  <SortableTh label="Account" col="account" sort={sort} onSort={toggleSort} filtered={!!filters.account} />
                  <Th label="Type" filtered={!!filters.type} />
                  <Th label="Status" filtered={!!filters.status} />
                  <SortableTh label="Billing name" col="billing_name" sort={sort} onSort={toggleSort} filtered={!!filters.billingName} />
                  <Th label="TIN" filtered={!!filters.tin} />
                  <Th label="Address" filtered={!!filters.address} />
                  <SortableTh label="Completeness" col="completeness" sort={sort} onSort={toggleSort} filtered={!!filters.completeness} />
                  <SortableTh label="Fee schedule" col="schedule" sort={sort} onSort={toggleSort} filtered={!!filters.schedule} />
                  <SortableTh label="Frequency" col="frequency" sort={sort} onSort={toggleSort} filtered={!!filters.frequency} />
                  <SortableTh label="VAT" col="vat" sort={sort} onSort={toggleSort} filtered={!!filters.vat} />
                  <SortableTh label="Resulting fee" col="fee" sort={sort} onSort={toggleSort} filtered={false} />
                  <Table.Th w={70}>
                    <Tooltip label="Select all shown accounts" withArrow>
                      <Checkbox size="xs" aria-label="Select all shown accounts"
                        checked={allSelected} indeterminate={someSelected && !allSelected}
                        onChange={toggleSelectAll} />
                    </Tooltip>
                  </Table.Th>
                </Table.Tr>
                <Table.Tr>
                  <Table.Th />
                  <Table.Th><ColumnTextFilter value={filters.account} onChange={(v) => setF('account', v)} placeholder="Account…" /></Table.Th>
                  <Table.Th><ColumnSelectFilter value={filters.type} onChange={(v) => setF('type', v)} options={[{ value: 'Direct', label: 'Direct' }, { value: 'Indirect', label: 'Indirect' }]} allLabel="Any" /></Table.Th>
                  <Table.Th><ColumnSelectFilter value={filters.status} onChange={(v) => setF('status', v)} options={[{ value: 'Active', label: 'Active' }, { value: 'Inactive', label: 'Inactive' }]} allLabel="Any" /></Table.Th>
                  <Table.Th><ColumnTextFilter value={filters.billingName} onChange={(v) => setF('billingName', v)} placeholder="Name…" /></Table.Th>
                  <Table.Th><ColumnTextFilter value={filters.tin} onChange={(v) => setF('tin', v)} placeholder="TIN…" /></Table.Th>
                  <Table.Th><ColumnTextFilter value={filters.address} onChange={(v) => setF('address', v)} placeholder="Address…" /></Table.Th>
                  <Table.Th><ColumnSelectFilter value={filters.completeness} onChange={(v) => setF('completeness', v)} options={[{ value: 'complete', label: 'Complete' }, { value: 'incomplete', label: 'Incomplete' }]} allLabel="Any" /></Table.Th>
                  <Table.Th><ColumnSelectFilter value={filters.schedule} onChange={(v) => setF('schedule', v)} options={scheduleOptions} allLabel="Any" /></Table.Th>
                  <Table.Th><ColumnSelectFilter value={filters.frequency} onChange={(v) => setF('frequency', v)} options={[{ value: 'monthly', label: 'Monthly' }, { value: 'quarterly', label: 'Quarterly' }, { value: 'semiannual', label: 'Semiannual' }, { value: SENTINEL_UNSET, label: 'Unset' }]} allLabel="Any" /></Table.Th>
                  <Table.Th><ColumnSelectFilter value={filters.vat} onChange={(v) => setF('vat', v)} options={vatOptions} allLabel="Any" /></Table.Th>
                  <Table.Th />
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {groups.length === 0 && (
                  <Table.Tr><Table.Td colSpan={COLSPAN}>
                    <Text size="sm" c="dimmed" py="sm">No accounts match the current filters.</Text>
                  </Table.Td></Table.Tr>
                )}
                {groups.map((g) => {
                  const open = isGroupOpen(g.agreementId);
                  const unbilled = g.rows.filter(isUnbilled).length;
                  const prospect = g.rows[0]?.agreement_status === 'Prospect';
                  const incomplete = g.rows.filter((r) => !r.is_complete).length;
                  const noAum = g.rows.filter((r) => r.missing_latest_aum).length;
                  const netTotal = g.rows.reduce((s, r) => s + (r.net_fee_cents ?? 0), 0);
                  const gKeys = g.rows.map(rowKey);
                  const gAll = gKeys.length > 0 && gKeys.every((k) => selected.has(k));
                  const gSome = gKeys.some((k) => selected.has(k));
                  const toggleGroupSelect = () =>
                    setSelected((prev) => { const n = new Set(prev); gKeys.forEach((k) => gAll ? n.delete(k) : n.add(k)); return n; });
                  return (
                    <Fragment key={g.agreementId}>
                      <Table.Tr
                        style={{
                          cursor: 'pointer',
                          backgroundColor: 'var(--mantine-color-gray-0)',
                          borderTop: '2px solid var(--mantine-color-gray-3)',
                        }}
                        onClick={() => toggleGroup(g.agreementId)}>
                        <Table.Td>
                          <ActionIcon variant="subtle" color="gray" size="sm" aria-label={open ? 'Collapse agreement' : 'Expand agreement'}>
                            {open ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
                          </ActionIcon>
                        </Table.Td>
                        <Table.Td colSpan={COLSPAN - 2}>
                          <Group gap="sm">
                            <Text size="sm" fw={700}>{g.code ?? '—'}</Text>
                            <Text size="sm" c="dimmed">{g.mnemonic}</Text>
                            {prospect && (
                              <Tooltip withArrow multiline w={280}
                                label="A Prospect can be given its fee schedule now, so its Annex I and IAA can be produced. It is not billed until it is promoted to Active.">
                                <Badge size="xs" variant="light" color="yellow">Prospect · not billed yet</Badge>
                              </Tooltip>
                            )}
                            <Text size="xs" c="dimmed">
                              {g.rows.length} account{g.rows.length === 1 ? '' : 's'}
                              {netTotal > 0 ? ` · €${fmtEurFromCents(netTotal)} net` : ''}
                            </Text>
                            {/* Loud per-agreement flags (Pedro 2026-10-14): each one can stop or
                                spoil this agreement's billing, so they are coloured badges, not grey text. */}
                            {unbilled > 0 && (
                              <Tooltip withArrow multiline w={280} label={ISSUE_TIP.unbilled}>
                                <Badge size="sm" color="red" variant="filled" leftSection={<IconAlertTriangle size={12} />}>
                                  {unbilled} unbilled
                                </Badge>
                              </Tooltip>
                            )}
                            {noAum > 0 && (
                              <Tooltip withArrow multiline w={280} label={ISSUE_TIP.missing_aum}>
                                <Badge size="sm" color="red" variant="light" leftSection={<IconAlertTriangle size={12} />}>
                                  {noAum} no 20th AuM
                                </Badge>
                              </Tooltip>
                            )}
                            {incomplete > 0 && (
                              <Tooltip withArrow multiline w={280} label={ISSUE_TIP.incomplete}>
                                <Badge size="sm" color="orange" variant="light" leftSection={<IconAlertTriangle size={12} />}>
                                  {incomplete} incomplete identity
                                </Badge>
                              </Tooltip>
                            )}
                          </Group>
                        </Table.Td>
                        <Table.Td onClick={(e) => e.stopPropagation()}>
                          <Tooltip label="Select all accounts in this agreement" withArrow>
                            <Checkbox size="xs" aria-label="Select all accounts in this agreement"
                              checked={gAll} indeterminate={gSome && !gAll} onChange={toggleGroupSelect} />
                          </Tooltip>
                        </Table.Td>
                      </Table.Tr>
                      {open && g.rows.map((r) => {
                        const key = rowKey(r);
                        const peersOpen = expandedPeers.has(key);
                        const editing = editingKey === key;
                        const canExpand = r.fee_schedule_id != null;
                        return (
                          <Fragment key={key}>
                            <Table.Tr>
                              <Table.Td>
                                {canExpand && (
                                  <ActionIcon variant="subtle" color="gray" size="sm"
                                    aria-label={peersOpen ? 'Hide basis group' : 'Show basis group'}
                                    onClick={() => togglePeers(key)}>
                                    {peersOpen ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
                                  </ActionIcon>
                                )}
                              </Table.Td>
                              <Table.Td><AccountCell row={r} /></Table.Td>
                              <Table.Td>
                                <Badge size="xs" variant="light" color={r.is_direct ? 'blue' : 'grape'}>
                                  {r.is_direct ? 'Direct' : 'Indirect'}
                                </Badge>
                              </Table.Td>
                              <Table.Td>
                                <StatusExplained entity="account" status={r.status}>
                                  <Badge size="xs" variant="light" color={r.status === 'Active' ? 'green' : 'gray'}>{r.status}</Badge>
                                </StatusExplained>
                              </Table.Td>
                              <Table.Td><FieldValue value={r.billing_name} drift={r.name_drift} /></Table.Td>
                              <Table.Td><FieldValue value={r.billing_tin} drift={r.tin_drift} /></Table.Td>
                              <Table.Td><FieldValue value={r.billing_address} drift={r.address_drift} /></Table.Td>
                              <Table.Td>
                                {r.is_complete
                                  ? <Badge color="green" variant="light" leftSection={<IconCheck size={12} />}>Complete</Badge>
                                  : <Badge color="orange" variant="light" leftSection={<IconCircleX size={12} />}>Incomplete</Badge>}
                              </Table.Td>
                              <Table.Td><ScheduleCell row={r} /></Table.Td>
                              <Table.Td>
                                <Text size="sm">{r.fee_frequency ? (FREQ_LABEL[r.fee_frequency] ?? r.fee_frequency) : '—'}</Text>
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm">{r.vat_rate_bps != null ? `${bpsToPct(r.vat_rate_bps).toFixed(2)}%` : '—'}</Text>
                              </Table.Td>
                              <Table.Td><FeeCell row={r} /></Table.Td>
                              <Table.Td>
                                <Group gap={4} wrap="nowrap">
                                  <Checkbox size="xs" aria-label="Select account"
                                    checked={selected.has(key)} onChange={() => toggleSelect(key)} />
                                  <Tooltip label={editing ? 'Close editor' : 'Edit billing details'} withArrow>
                                    <ActionIcon variant="subtle" color={editing ? 'azul' : 'gray'} size="sm"
                                      aria-label="Edit billing details"
                                      onClick={() => setEditingKey(editing ? null : key)}>
                                      <IconPencil size={16} />
                                    </ActionIcon>
                                  </Tooltip>
                                </Group>
                              </Table.Td>
                            </Table.Tr>
                            {(editing || peersOpen) && (
                              <Table.Tr>
                                <Table.Td />
                                <Table.Td colSpan={COLSPAN - 1}>
                                  {editing
                                    ? <BillingRowEditor row={r} onClose={() => setEditingKey(null)} />
                                    : <BasisPeersDetail agreementId={r.agreement_id} accountId={r.account_id} />}
                                </Table.Td>
                              </Table.Tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </Fragment>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>

          {groups.some((g) => g.rows.some((r) => r.fee_period_month)) && (
            <Text size="xs" c="dimmed">
              Resulting fee = net (ex-VAT) per billing period, priced at each account's latest
              month with AuM; the %/yr is the annualised effective rate. An assigned account
              with no AuM shows "no AuM".
            </Text>
          )}
        </>
      )}
    </Stack>
  );
}

function Th({ label, filtered }: { label: string; filtered: boolean }) {
  return <Table.Th><FilteredColumnLabel active={filtered}><Text size="sm" fw={600}>{label}</Text></FilteredColumnLabel></Table.Th>;
}

function SortableTh({
  label, col, sort, onSort, filtered,
}: {
  label: string; col: SortKey;
  sort: { key: SortKey; dir: 'asc' | 'desc' }; onSort: (k: SortKey) => void; filtered: boolean;
}) {
  const active = sort.key === col;
  const Icon = !active ? IconSelector : sort.dir === 'asc' ? IconChevronUp : IconChevronDown;
  return (
    <Table.Th>
      <UnstyledButton onClick={() => onSort(col)} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <FilteredColumnLabel active={filtered}><Text size="sm" fw={600}>{label}</Text></FilteredColumnLabel>
        <Icon size={14} style={{ opacity: active ? 1 : 0.4 }} />
      </UnstyledButton>
    </Table.Th>
  );
}

/** Account identity cell — bank · number + insurer·policy subline (Type/Status are
 *  their own columns now, so they carry per-column filters). */
function AccountCell({ row }: { row: FirmWideBillingRow }) {
  return (
    <Stack gap={2}>
      <Text size="sm" fw={500}>{accountLabel(row)}</Text>
      {!row.is_direct && (row.insurer || row.policy_number) && (
        <Text size="xs" c="dimmed">
          {row.insurer ?? 'Insurer'}{row.policy_number ? ` · ${row.policy_number}` : ''}
        </Text>
      )}
    </Stack>
  );
}

/** Schedule name + the two basis-vs-assignment info flags, pills UNDER the name
 *  (Pedro's smoke polish — pills sit beneath the field they refer to). */
function ScheduleCell({ row }: { row: FirmWideBillingRow }) {
  return (
    <Stack gap={2}>
      {row.fee_schedule_name ? <Text size="sm">{row.fee_schedule_name}</Text> : <Text size="sm" c="dimmed">Unassigned</Text>}
      {row.assigned_not_in_basis && (
        <Tooltip withArrow multiline w={260}
          label="Billed under this schedule but NOT part of its basis group — it pays the family rate without feeding it.">
          <Badge size="xs" color="grape" variant="light">not in basis</Badge>
        </Tooltip>
      )}
      {row.in_basis_billed_elsewhere && (
        <Tooltip withArrow multiline w={260}
          label="This account feeds another schedule's basis group but is billed under a different schedule.">
          <Badge size="xs" color="grape" variant="light">feeds another basis</Badge>
        </Tooltip>
      )}
    </Stack>
  );
}

/** Resulting-fee cell — € net + %/yr, or "no AuM" when assigned but not computing, or
 *  a dimmed em-dash when unassigned. */
function FeeCell({ row }: { row: FirmWideBillingRow }) {
  if (row.missing_latest_aum) {
    return (
      <Tooltip withArrow multiline w={240}
        label="Assigned a fee schedule but has no 20th-of-month AuM for the latest month — it will not compute a fee until AuM is entered.">
        <Badge color="orange" variant="light" leftSection={<IconAlertTriangle size={12} />}>no AuM</Badge>
      </Tooltip>
    );
  }
  if (row.net_fee_cents == null) return <Text size="sm" c="dimmed">—</Text>;
  return (
    <Stack gap={0}>
      <Text size="sm">€{fmtEurFromCents(row.net_fee_cents)}</Text>
      <Group gap={4}>
        <Text size="xs" c="dimmed">
          {row.effective_rate_bps != null ? `${bpsToPct(row.effective_rate_bps).toFixed(2)}%/yr` : 'fixed'}
        </Text>
        {row.fee_period_month && <Text size="xs" c="dimmed">· {fmtMonthYear(row.fee_period_month)}</Text>}
      </Group>
    </Stack>
  );
}

/** Row-expand content (mini-design A): the account's basis peers. Lazily fetched. */
function BasisPeersDetail({ agreementId, accountId }: { agreementId: string; accountId: string }) {
  const q = useQuery({
    queryKey: ['basis-peers', agreementId, accountId],
    queryFn: () => accountBillingApi.basisPeers(agreementId, accountId),
  });
  if (q.isLoading) return <Group gap="xs" py="xs"><Loader size="xs" /><Text size="sm" c="dimmed">Loading basis group…</Text></Group>;
  if (q.isError) return <Text size="sm" c="red" py="xs">Could not load the basis group.</Text>;
  const peers: BasisPeer[] = q.data ?? [];
  if (peers.length === 0) {
    return (
      <Text size="sm" c="dimmed" py="xs">
        This schedule bills on the account's own AuM — no consolidated basis group.
      </Text>
    );
  }
  return (
    <Stack gap={4} py="xs">
      <Text size="sm" fw={500}>Rate blended over {peers.length} account{peers.length === 1 ? '' : 's'}:</Text>
      <Group gap="xs" wrap="wrap">
        {peers.map((p) => (
          <Badge key={p.account_id} variant="light" color="gray" size="sm">
            {([p.agreement_code, p.agreement_mnemonic].filter(Boolean).join(' · ') || '—')}
            {' — '}{p.account_label ?? 'account'}{p.is_direct ? '' : ' (indirect)'}
          </Badge>
        ))}
      </Group>
    </Stack>
  );
}

/** A read-mode billing field value with an orange drift pill UNDER the value
 *  (Pedro's smoke polish). Empty renders a dimmed em-dash. */
function FieldValue({ value, drift }: { value: string | null; drift: boolean }) {
  return (
    <Stack gap={2}>
      {value ? <Text size="sm">{value}</Text> : <Text size="sm" c="dimmed">—</Text>}
      {drift && (
        <Tooltip label="Differs from the linked party's current value" withArrow>
          <Badge size="xs" color="orange" variant="light" leftSection={<IconAlertTriangle size={10} />}>drift</Badge>
        </Tooltip>
      )}
    </Stack>
  );
}
